import { Form, FormikProps } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { certificateActions, getCertificate, selectCertificate, selectCertificateState } from '../../store/entities/certificate';
import { InputTextWithLang } from '../common/form/InputTextWithLang';
import { documentListActions } from '../../store/entities/documentList';
import { QcsSwitch } from '../common/form/QcsSwitch';
import { Select } from '../common/form/Select';
import { QcsMenuItem } from '../common/basic/QcsMenuItem';
import { FormContainer } from '../common/form/FormContainer';
import { DetailHeader } from '../common/DetailHeader';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { SubmitAndNewButtons } from '../common/form/SubmitAndNewButtons';
import { Validator } from '../../models/common';
import { fixNameInitValue } from '../../utils/format';
import { useBack } from '../../hooks/useBack';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { CertificateDto } from '@api/models/suppliers/CertificateDto';
import { EntityState } from '@api/models/enums/EntityState';
import { certificateService } from '@api/services/services';
import { resources } from '@api/resources';
import { ShowResourceContent } from '../ShowResourceContent';
import { QcsTypography } from '../common/basic/QcsTypography';
import { DocumentTypes } from './DocumentTypes';

interface DocumentDetailForm extends CertificateDto {
  saveAndNew?: boolean;
}

export const DocumentDetail: FC = () => {
  const { i18n, t } = useTranslation();
  const { backTo, goBack } = useBack('/documents');
  const { documentId } = useParams();
  const document = useAppSelector(selectCertificate);
  const dispatch = useAppDispatch();
  const formikRef = useRef<FormikProps<DocumentDetailForm> | null>(null);
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const [showTypes, setShowTypes] = useState(false);
  const [typesCount, setTypesCount] = useState(0);
  const [prevDocument, setPrevDocument] = useState(document);

  if (document !== prevDocument) {
    setPrevDocument(document);
    setShowTypes((document.types?.length ?? 0) > 0);
    setTypesCount(document.types?.length ?? 0);
  }

  useEffect(() => {
    if (documentId === 'new') {
      dispatch(certificateActions.default());
    } else {
      dispatch(getCertificate(documentId!));
    }
  }, [documentId, dispatch]);

  const handleSubmit = async (data: DocumentDetailForm) => {
    documentId === 'new' ? await certificateService.create(data) : await certificateService.update(documentId!, data);

    dispatch(documentListActions.reload(true));

    if (data.saveAndNew) {
      formikRef.current?.resetForm();
    } else {
      goBack();
    }
  };

  const validator: Validator = {};

  if (companyCustomization?.applicationLanguages.includes('cs')) {
    validator['name'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('en')) {
    validator['nameEn'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('de')) {
    validator['nameDe'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('pl')) {
    validator['namePl'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('ru')) {
    validator['nameRu'] = validations.stringRequired(t);
  }

  return (
    <>
      <DetailHeader title="document.detailTitle" backTo={backTo} />
      <ShowResourceContent resource={resources.getCertificate} fetchStateSelector={selectCertificateState}>
        <FormikDebounce<DocumentDetailForm>
          initialValues={{
            ...document,
            ...fixNameInitValue(i18n, document),
          }}
          validationSchema={Yup.object().shape(validator)}
          onSubmit={handleSubmit}
          innerRef={formikRef}
          enableReinitialize={true}
        >
          <Form>
            <FormContainer>
              <InputTextWithLang isNew={documentId === 'new'} label={t('document.name')} required />
              <QcsSwitch name="expiryDateRequired" label={t('document.expiryDateRequired')} />

              <Select name="state" label={t('document.state')} disabled={!document.stateChangeEnabled}>
                <QcsMenuItem value={EntityState.Active}>{t('entityState.ACTIVE')}</QcsMenuItem>
                <QcsMenuItem value={EntityState.Inactive}>{t('entityState.INACTIVE')}</QcsMenuItem>
              </Select>

              {documentId === 'new' ? (
                <QcsTypography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('document.types.newDocumentHint')}
                </QcsTypography>
              ) : (
                <div>
                  <QcsTypography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t('document.types.heading')}
                  </QcsTypography>
                  <DocumentTypes certificateId={documentId!} initialTypes={document.types ?? []} onCountChange={setTypesCount} />
                </div>
              )}

              <SubmitAndNewButtons id={documentId} errorResource={[resources.createCertificate, resources.updateCertificate]} />
            </FormContainer>
          </Form>
        </FormikDebounce>
      </ShowResourceContent>
    </>
  );
};
